import { useEffect, useState } from "react";
import parse from "html-react-parser";
import SelectVariant from "@components/select-variant";
import AddToCartButton from "@components/ui/add-to-cart-button";
import { useShopifyCart } from "@providers/cart/CartProvider";
import { SharedProductProps } from "../Product";
import * as Styled from "../Product.styled";
import { firstAvailableVariant } from "@utils/firstAvailableVariant";
import { formatPrice, formatPriceComparison, productDisplayPrice } from "@utils/pricing";
import { Shopify } from "@models/shopify.model";
import { SanitySyncVariant } from "@models/product.model";
import { getVariantNodes } from "@utils/getVariantNodes";
import { events } from "@providers/analytics/analytics";
import { useUserProfile } from "@providers/profile";

export interface CheckoutInfoProps {
  variant_id: string;
  price: string;
}

const AccessoryProduct = ({ productDocument, storefrontProduct }: SharedProductProps) => {
  const {
    profileState: { authState },
  } = useUserProfile();
  const {
    title,
    subtitle,
    variants: documentVariants,
    priceRange,
    descriptionHtml,
  } = productDocument;
  const { addItem, setCartVisible, checkout } = useShopifyCart();
  const [variants, setVariants] = useState<Shopify.ProductVariant[] | SanitySyncVariant[]>(
    documentVariants
  );
  /**
   * A null defaultVariant means no variants are available for sale.
   */
  const defaultVariant = firstAvailableVariant(storefrontProduct);
  /**
   * Don't set the initial value of the variant to the defaultVariant.
   * the Select needs to default to a label rather than the first available
   * variant.
   */
  const [variantId, setVariantId] = useState<Shopify.ProductVariantId | "">("");

  /**
   * Even if a product has no options, it still has a single default variant.
   * Check for more than 1 variant before rendering the variant select.
   */
  const hasMultipleVariants = variants.length > 1;
  /**
   * The defaultVariantId should be set to the defaultVariant.id if:
   *   - The product doesn't have multiple variants AND
   *   - There is a defaultVariant available
   *
   * In all other cases, default to an empty string and require the user
   * to make a selection. useEffect(() => {}, [variantId]) will call
   * setCheckoutInfo() with a variantId when appropriate.
   */
  const defaultVariantId = !hasMultipleVariants && defaultVariant ? defaultVariant.id : "";
  const defaultCheckout = {
    variant_id: defaultVariantId,
    price: defaultVariant
      ? formatPrice(Number(defaultVariant?.price?.amount))
      : productDisplayPrice(priceRange),
  };
  const [checkoutInfo, setCheckoutInfo] = useState<CheckoutInfoProps>(defaultCheckout);
  /**
   * Disable the Add to Cart button if no variant is selected/available.
   */
  const disabledCartBtn = hasMultipleVariants && !variantId;
  /**
   * Update the checkoutInfo state if necessary when the variant is changed.
   */
  useEffect(() => {
    if (!storefrontProduct || checkoutInfo.variant_id === variantId) return;
    const activeVariant = getVariantNodes(storefrontProduct).find(({ id }) => id === variantId);

    if (activeVariant) {
      setCheckoutInfo({
        variant_id: activeVariant?.id,
        price: formatPrice(Number(activeVariant.price.amount)),
      });
    } else {
      const { minVariantPrice, maxVariantPrice } = storefrontProduct?.priceRange;

      const price = minVariantPrice
        ? productDisplayPrice({
            minVariantPrice: Number(minVariantPrice.amount),
            maxVariantPrice: Number(maxVariantPrice.amount),
          })
        : defaultCheckout.price;

      setCheckoutInfo({
        variant_id: "",
        price,
      });
    }
  }, [variantId]);

  useEffect(() => {
    /**
     * Set the variant without user input when there is only
     * a default variant on the product.
     */
    const needsVariantAssigned = !hasMultipleVariants && storefrontProduct && !variantId;
    if (needsVariantAssigned) setVariantId(defaultVariant?.id || "");
    /**
     * Upgrade to run-time pricing.
     * Set the variants as the storefrontProduct variants if available
     */
    if (storefrontProduct) {
      const { minVariantPrice, maxVariantPrice } = storefrontProduct.priceRange;

      events.product.viewed(authState.value, checkout, defaultVariant, true);

      setCheckoutInfo({
        variant_id: checkoutInfo.variant_id,
        price: productDisplayPrice({
          minVariantPrice: Number(minVariantPrice.amount),
          maxVariantPrice: Number(maxVariantPrice.amount),
        }),
      });
      setVariants(getVariantNodes(storefrontProduct));
    }
  }, [storefrontProduct]);
  /**
   *
   * @desc Update only the variant. When the variant is updated,
   *     the checkout info will be updated accordingly by the
   *     useEffect(() => ..., [variant]) hook.
   */
  const handleVariantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setVariantId(e.target.value as Shopify.ProductVariantId);
  };

  const handleClickAddToCart = (variantId: string) => {
    addItem(authState, {
      variantId,
      quantity: 1,
      customAttributes: [
        {
          key: "_productType", // prepend an underscore to the key to stop the attribute from dispalying in shopify checkout pages
          value: "accessory",
        },
      ],
    });

    setCartVisible(true);
  };

  return (
    <>
      <Styled.Details>
        <div>
          <Styled.Heading>
            <h1 className="h3">{title}</h1>
            {subtitle?.value && <p>{subtitle.value}</p>}
          </Styled.Heading>
          <Styled.Meta>
            <Styled.ProductDescription>{parse(descriptionHtml)}</Styled.ProductDescription>
          </Styled.Meta>
        </div>
        <Styled.SubItemSelectBlock>
          <Styled.Footer>
            {hasMultipleVariants && (
              <>
                <Styled.SelectRow>
                  <div className="variant-select">
                    <SelectVariant
                      id={productDocument._id}
                      title={title}
                      variants={variants}
                      value={variantId}
                      onChange={handleVariantChange}
                    />
                  </div>
                </Styled.SelectRow>
              </>
            )}
            <AddToCartButton
              onClick={() => handleClickAddToCart(checkoutInfo.variant_id)}
              disabled={disabledCartBtn}
              productId={checkoutInfo.variant_id}>
              <>
                {checkoutInfo.price}
                <span>&nbsp;&nbsp;|&nbsp;&nbsp;</span>
                Add To Cart
              </>
            </AddToCartButton>
          </Styled.Footer>
        </Styled.SubItemSelectBlock>
      </Styled.Details>
    </>
  );
};

export default AccessoryProduct;
